import { BackButton } from "@publicPortal/components/BackButton/BackButton";
import Board from "@publicPortal/components/board/Board";
import ResponsiveAppBar from "@publicPortal/components/responsiveAppBar/ResponsiveAppBar";
import { Filter, FilterItem } from "@shared/mirage/entityTable/Filter";
import PlanningApplicationsTable from "../components/PlanningApplicationsTable";
import EnforcementAppealsTable from "../components/EnforcementAppealsTable";
import EnforcementNoticeTable from "../components/EnforcementNoticeTable";
import PlanningAppealsTable from "../components/PlanningAppealsTable";
import BuildingRegulationsTable from "../components/BuildingRegulationsTable";
import SaveBookmark from "@publicPortal/components/customBookmark/SaveBookmark";
import SaveSearchModal from "../SaveSearchModal";
import { memo, useCallback, useMemo, useState } from "react";
import { UserService } from "@publicPortal/services/UserService";
import { IUser } from "@shared/mirage/core/contracts/IUser";
import SavedSearchService from "@publicPortal/services/SavedSearchService";
import { ISavedSearch } from "@shared/publicportal/contracts/ISavedSearch";
import _ from "lodash";
import useAlertService from "@mirage/hooks/useAlertService";
import CompetentPersonsSchemeTable from "../components/CompetentPersonsSchemeTable";
import useSessionStorage from "@mirage/hooks/useSessionStorage";
import { PPConstants } from "@shared/publicportal/constants";
import Stack from "@mui/material/Stack";

const PropertySearchResult = memo(() => {
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const { clientSettings } = sessionValue;
    const user: Partial<IUser> = UserService.getLoggedInUser();
    const sp = useMemo(() => new URL(window.location.href).searchParams, []);
    const params = useMemo(() => Array.from(sp), [sp]);
    const showAlert = useAlertService();
    const [searchName, setSearchName] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const handleOpenModal = useCallback(() => setOpenModal(true), []);
    const handleCloseModal = useCallback(() => {
        setSearchName("");
        setOpenModal(false);
    }, []);
    const filter = useMemo(() => {
        const filter = { parts: [] } as Filter;
        const filterItems: FilterItem[] = [];

        params.forEach(([key, value]) => {
            filterItems.push({ columnName: key, value, operator: "=" });
        });

        filter.parts.push({ filterItems: filterItems });
        return filter;
    }, [params]);
    const handleSaveSearch = useCallback(async () => {
        const saveSearchService = new SavedSearchService();
        const criteria = _.fromPairs(params);

        const SaveRequest: Omit<ISavedSearch, "userId"> = {
            name: searchName,
            criteria: criteria,
            searchType: "PropertySearch",
        };

        const result = await saveSearchService.createSavedSearch(SaveRequest);
        if (result.errorMsg) {
            showAlert(result.errorMsg, "error");
        } else {
            showAlert("Search saved successfully.", "success");
        }
    }, [params, searchName, showAlert]);

    return (
        <>
            <ResponsiveAppBar />
            <Board size="big" headerText="Property Search">
                <BackButton />
                <SaveSearchModal handleSaveSearch={handleSaveSearch} isOpen={openModal} onClose={handleCloseModal} searchName={searchName} setSearchName={setSearchName} />
                <SaveBookmark action={handleOpenModal} tooltipMessage="Save Search" userId={user?.id} />
                <Stack gap="2rem">
                    {clientSettings?.PROPERTY_SEARCH_BUILDING_REGULATIONS ? <BuildingRegulationsTable filter={filter} title="Building Regulations" /> : null}
                    {clientSettings?.PROPERTY_SEARCH_COMPETENT_PERSONS_SCHEME ? <CompetentPersonsSchemeTable filter={filter} title="Competent Persons Scheme" /> : null}
                    {clientSettings?.PROPERTY_SEARCH_PLANNING_APPLICATIONS ? <PlanningApplicationsTable filter={filter} title="Planning Applications" /> : null}
                    {clientSettings?.PROPERTY_SEARCH_PLANNING_APPEALS ? <PlanningAppealsTable filter={filter} title="Planning Appeals" /> : null}
                    {clientSettings?.PROPERTY_SEARCH_ENFORCEMENT_NOTICES ? <EnforcementNoticeTable filter={filter} title="Enforcement Notices" /> : null}
                    {clientSettings?.PROPERTY_SEARCH_ENFORCEMENT_APPEALS ? <EnforcementAppealsTable filter={filter} title="Enforcement Appeals" /> : null}
                </Stack>
            </Board>
        </>
    );
});

export default PropertySearchResult;
